import {
  AccessoryCmd as AccessoryCmdCodec,
  AutonomousMode as AutonomousModeCodec,
  ControlRequest as ControlRequestCodec,
  ControlStatus as ControlStatusCodec,
  KeyValue as KeyValueCodec,
  KeyValueArray as KeyValueArrayCodec,
  LateralCmd as LateralCmdCodec,
  LongitudinalCmd as LongitudinalCmdCodec,
  Mission as MissionCodec,
  MissionCmd as MissionCmdCodec,
  MissionCmd_Type,
  ControlStatus_TransferStatus,
  Path as PathCodec,
  SystemCmd as SystemCmdCodec,
  SystemCmd_Command,
  VehicleState as VehicleStateCodec,
  Waypoints as WaypointsCodec,
} from "@brain/proto/generated/brain_msgs";
import {
  AckInfo as AckInfoCodec,
  RemoteControlStatus as RemoteControlStatusCodec,
  RemoteControlStatus_RemoteType,
  type AckInfo,
} from "@brain/proto/generated/brain_system_msgs";
import {
  UserConfiguration as UserConfigurationCodec,
  type UserConfiguration,
  VehicleGenericInfo as VehicleGenericInfoCodec,
  type VehicleGenericInfo,
} from "@brain/proto/generated/brain_sensor_msgs";
import {
  AccessoryCmd,
  AccessoryCmd_DoorLock,
  AccessoryCmd_DoorState,
  AutonomousMode,
  AutonomousMode_OperationMode,
  ControlRequest,
  ControlRequest_CtrlType,
  ControlRequest_RequestStatus,
  ControlStatus as ControlStatusType,
  ControlStatus_Status,
  Gear_GearType,
  KeyValue,
  KeyValue_Type,
  KeyValueArray,
  LateralCmd,
  LateralCmd_Mode,
  LongitudinalCmd,
  LongitudinalCmd_Mode,
  Mission,
  RemoteControlStatus,
  TurnSignal_Signal,
  VehicleState,
} from "@brain/proto";
import { String as StdStringCodec } from "@brain/proto/generated/std_msgs";
import type { PathNode, Waypoints } from "@brain/proto/generated/brain_msgs";
import {
  ALL_CAMERA_SETTINGS_TOPICS,
  BRAIN_TOPICS_COMMAND,
  BRAIN_TOPICS_CONTROL,
  BRAIN_TOPICS_REMOTE_STATUS,
  BRAIN_TOPICS_SYSTEM,
  BRAIN_TOPIC_TYPE_NAMES,
  type CameraSettingsTopic,
} from "../config/mqtt_config";
import type { CameraFpsMap } from "../types/camera_fps";

const topicSeqMap = new Map<string, number>();

const DEFAULT_TEST_LAB_PATH_NODES: PathNode[] = [
  {
    pose: { position: { x: 0, y: 0, z: 0 }, orientation: undefined },
    dir: 0,
  },
  {
    pose: { position: { x: 10, y: 5, z: 0 }, orientation: undefined },
    dir: 0,
  },
  {
    pose: { position: { x: 20, y: 0, z: 0 }, orientation: undefined },
    dir: 0,
  },
];

export interface LateralCommandState {
  mode: LateralCmd_Mode;
  steeringAngle: number;
  steeringAngleRate: number;
  steeringTorque: number;
  torqueLimit: number;
  lateralError: number;
  headingError: number;
}

export interface LongitudinalCommandState {
  mode: LongitudinalCmd_Mode;
  velocity: number;
  acceleration: number;
  torque: number;
  accPos: number;
  accTorque: number;
  brkPos: number;
  brkTorque: number;
  distanceError: number;
  speedError: number;
  gearStatus: number;
}

export interface AccessoryCommandState {
  signalStatus: number;
  doorLock: AccessoryCmd_DoorLock;
  doorOpenRight: AccessoryCmd_DoorState;
  doorOpenLeft: AccessoryCmd_DoorState;
}

export interface MediaCommandState {
  key: string;
  direction: string;
  type: KeyValue_Type;
}

export interface KeyValueState {
  key: string;
  value: string;
  type: KeyValue_Type;
}

export type RemoteSystemConnectionStatus =
  | "both"
  | "mqtt"
  | "webrtc"
  | "none";

export interface RemoteSystemStatusState {
  cameraFps?: CameraFpsMap;
  connection: RemoteSystemConnectionStatus;
}

export interface UserConfigurationCommandState {
  maxSpeedKph: number;
  maxSteeringTorque: number;
  maxAcceleration: number;
  maxDeceleration: number;
  maxJerk: number;
}

export interface AutomodeCommandState {
  enabled: boolean;
  autonomousMode: number;
  errorCode: number;
  warningCode: number;
  activator: number;
  userDefinedButton0: number;
  userDefinedButton1: number;
  buttonPushed: number;
  mrmStatus: number;
}

const DEFAULT_LATERAL_COMMAND_STATE: LateralCommandState = {
  mode: LateralCmd_Mode.ANGLE,
  steeringAngle: 0,
  steeringAngleRate: 0,
  steeringTorque: 0,
  torqueLimit: 0,
  lateralError: 0,
  headingError: 0,
};

const DEFAULT_LONGITUDINAL_COMMAND_STATE: LongitudinalCommandState = {
  mode: LongitudinalCmd_Mode.MODE_PEDAL_POSITION,
  velocity: 0,
  acceleration: 0,
  torque: 0,
  accPos: 0,
  accTorque: 0,
  brkPos: 0,
  brkTorque: 0,
  distanceError: 0,
  speedError: 0,
  gearStatus: 0,
};

const DEFAULT_ACCESSORY_COMMAND_STATE: AccessoryCommandState = {
  signalStatus: 0,
  doorLock: AccessoryCmd_DoorLock.UNLOCK,
  doorOpenRight: AccessoryCmd_DoorState.INACTIVE,
  doorOpenLeft: AccessoryCmd_DoorState.INACTIVE,
};

const DEFAULT_MEDIA_COMMAND_STATE: MediaCommandState = {
  key: "media.camera.side_view.request",
  direction: "no",
  type: KeyValue_Type.TYPE_STRING,
};

const DEFAULT_KEY_VALUE_STATE: KeyValueState = {
  key: "cockpit_hw",
  value: "disconnected",
  type: KeyValue_Type.TYPE_STRING,
};

const DEFAULT_USER_CONFIGURATION_COMMAND_STATE: UserConfigurationCommandState = {
  maxSpeedKph: 0,
  maxSteeringTorque: 0,
  maxAcceleration: 0,
  maxDeceleration: 0,
  maxJerk: 0,
};

const DEFAULT_AUTOMODE_COMMAND_STATE: AutomodeCommandState = {
  enabled: false,
  autonomousMode: 0,
  errorCode: 0,
  warningCode: 0,
  activator: 0,
  userDefinedButton0: 0,
  userDefinedButton1: 0,
  buttonPushed: 0,
  mrmStatus: 0,
};

export const SYSTEM_COMMANDS = {
  NA: SystemCmd_Command.COMMAND_NA,
  ESTOP: SystemCmd_Command.COMMAND_ESTOP,
  RESET: SystemCmd_Command.COMMAND_RESET,
} as const;

export type SystemCommandCode =
  (typeof SYSTEM_COMMANDS)[keyof typeof SYSTEM_COMMANDS];

function buildHeader(topic: string) {
  const timestampMs = Date.now();
  const seq = topicSeqMap.get(topic) || 0;
  topicSeqMap.set(topic, seq + 1);
  return {
    stamp: new Date(timestampMs),
    uuid: `${topic}_${timestampMs}`,
    seq,
  };
}

function mapGearStatusToProto(gearStatus: number): Gear_GearType {
  if (gearStatus === 1) return Gear_GearType.GEAR_P;
  if (gearStatus === 2) return Gear_GearType.GEAR_R;
  if (gearStatus === 3) return Gear_GearType.GEAR_N;
  if (gearStatus === 4) return Gear_GearType.GEAR_D;
  return Gear_GearType.INACTIVE;
}

function mapTurnSignalStatusToProto(signalStatus: number): TurnSignal_Signal {
  if (signalStatus === 0) return TurnSignal_Signal.OFF;
  if (signalStatus === 1) return TurnSignal_Signal.LEFT;
  if (signalStatus === 2) return TurnSignal_Signal.RIGHT;
  if (signalStatus === 3) return TurnSignal_Signal.HAZARD;
  return TurnSignal_Signal.UNRECOGNIZED;
}

function mergeState<T>(defaults: T, input?: Partial<T>): T {
  return {
    ...defaults,
    ...(input || {}),
  };
}

const REMOTE_SYSTEM_FPS_FIELDS = [
  { key: "front-fps", slot: "front-cam" },
  { key: "rear-fps", slot: "rear-cam" },
  { key: "left-fps", slot: "left-cam" },
  { key: "right-fps", slot: "right-cam" },
  { key: "left-rear-fps", slot: "left-rear-cam" },
  { key: "right-rear-fps", slot: "right-rear-cam" },
] as const;

function formatRemoteSystemFps(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "0";
  }

  return String(Math.round(value * 10) / 10);
}

export interface MessageHeaderLike {
  stamp?: Date;
  uuid?: string;
  seq?: number;
}

export interface HeaderEnvelopeLike {
  typeName?: string;
  payload: Uint8Array;
}

function normalizeHeader(header: any): MessageHeaderLike | null {
  if (!header || typeof header !== "object") {
    return null;
  }

  return {
    stamp:
      header.stamp instanceof Date
        ? header.stamp
        : header.stamp
          ? new Date(header.stamp)
          : undefined,
    uuid: typeof header.uuid === "string" ? header.uuid : undefined,
    seq: typeof header.seq === "number" ? header.seq : undefined,
  };
}

export function encodeLateralCmd(
  input: Partial<LateralCommandState> = {},
): Uint8Array {
  const state = mergeState(DEFAULT_LATERAL_COMMAND_STATE, input);
  return LateralCmdCodec.encode(
    LateralCmdCodec.create({
      header: buildHeader(BRAIN_TOPICS_COMMAND.LATERAL),
      mode: state.mode,
      steeringAngle: Number(state.steeringAngle || 0),
      steeringAngleRate: Number(state.steeringAngleRate || 0),
      steeringTorque: Number(state.steeringTorque || 0),
      torqueLimit: Number(state.torqueLimit || 0),
      lateralError: Number(state.lateralError || 0),
      headingError: Number(state.headingError || 0),
    }),
  ).finish();
}

export function encodeLongitudinalCmd(
  input: Partial<LongitudinalCommandState> = {},
): Uint8Array {
  const state = mergeState(DEFAULT_LONGITUDINAL_COMMAND_STATE, input);
  const gearType = mapGearStatusToProto(Number(state.gearStatus || 0));

  return LongitudinalCmdCodec.encode(
    LongitudinalCmdCodec.create({
      header: buildHeader(BRAIN_TOPICS_COMMAND.LONGITUDINAL),
      mode: state.mode,
      velocity: Number(state.velocity || 0),
      acceleration: Number(state.acceleration || 0),
      torque: Number(state.torque || 0),
      accPos: Number(state.accPos || 0),
      accTorque: Number(state.accTorque || 0),
      brkPos: Number(state.brkPos || 0),
      brkTorque: Number(state.brkTorque || 0),
      distanceError: Number(state.distanceError || 0),
      speedError: Number(state.speedError || 0),
      gear:
        gearType === Gear_GearType.INACTIVE
          ? undefined
          : { gear: gearType },
    }),
  ).finish();
}

export function encodeAccessoryCmd(
  input: Partial<AccessoryCommandState> = {},
): Uint8Array {
  const state = mergeState(DEFAULT_ACCESSORY_COMMAND_STATE, input);
  return AccessoryCmdCodec.encode(
    AccessoryCmdCodec.create({
      header: buildHeader(BRAIN_TOPICS_COMMAND.ACCESSORY),
      signal: {
        signal: mapTurnSignalStatusToProto(Number(state.signalStatus || 0)),
      },
      doorLock: state.doorLock,
      doorOpenRight: state.doorOpenRight,
      doorOpenLeft: state.doorOpenLeft,
    }),
  ).finish();
}

export function encodeSystemCmd(command: SystemCommandCode): Uint8Array {
  return SystemCmdCodec.encode(
    SystemCmdCodec.create({
      header: buildHeader(BRAIN_TOPICS_COMMAND.SYSTEM),
      command,
    }),
  ).finish();
}

export function encodeAckInfo(
  topic: string = BRAIN_TOPICS_SYSTEM.REMOTE_ACK_INFO,
  ack?: Partial<ReturnType<typeof buildHeader>>,
): Uint8Array {
  const fallbackAck = buildHeader(`${topic}:ack`);
  return AckInfoCodec.encode(
    AckInfoCodec.create({
      header: buildHeader(topic),
      ack: {
        stamp: ack?.stamp ?? fallbackAck.stamp,
        uuid: ack?.uuid ?? fallbackAck.uuid,
        seq: ack?.seq ?? fallbackAck.seq,
      },
    }),
  ).finish();
}

export function encodeMediaCmd(
  input: Partial<MediaCommandState> = {},
): Uint8Array {
  const state = mergeState(DEFAULT_MEDIA_COMMAND_STATE, input);
  return encodeKeyValue({
    key: state.key,
    value: String(state.direction || "no"),
    type: state.type,
  });
}

export function encodeKeyValue(
  input: Partial<KeyValueState> = {},
  _topic: string = BRAIN_TOPICS_REMOTE_STATUS.COCKPIT_HARDWARE,
): Uint8Array {
  const state = mergeState(DEFAULT_KEY_VALUE_STATE, input);
  return KeyValueCodec.encode(
    KeyValueCodec.create({
      key: state.key,
      value: String(state.value || "disconnected"),
      type: state.type,
    }),
  ).finish();
}

export function encodeRemoteSystemStatus(
  input: RemoteSystemStatusState,
): Uint8Array {
  const cameraFps = input.cameraFps || {};
  return KeyValueArrayCodec.encode(
    KeyValueArrayCodec.create({
      header: buildHeader(BRAIN_TOPICS_REMOTE_STATUS.SYSTEM),
      list: [
        ...REMOTE_SYSTEM_FPS_FIELDS.map(({ key, slot }) => ({
          key,
          value: formatRemoteSystemFps(cameraFps[slot]),
          type: KeyValue_Type.TYPE_DOUBLE,
        })),
        {
          key: "vehicle connection",
          value: input.connection,
          type: KeyValue_Type.TYPE_STRING,
        },
      ],
    }),
  ).finish();
}

export function encodeUserConfigurationCmd(
  input: Partial<UserConfigurationCommandState> = {},
): Uint8Array {
  const state = mergeState(DEFAULT_USER_CONFIGURATION_COMMAND_STATE, input);
  return UserConfigurationCodec.encode(
    UserConfigurationCodec.create({
      header: buildHeader(BRAIN_TOPICS_COMMAND.USER_CONFIGURATION),
      maxSpeed: Number(state.maxSpeedKph || 0) * (1000 / 3600),
      maxSteeringTorque: Number(state.maxSteeringTorque || 0),
      maxAcceleration: Number(state.maxAcceleration || 0),
      maxDeceleration: Number(state.maxDeceleration || 0),
      maxJerk: Number(state.maxJerk || 0),
    }),
  ).finish();
}

export function encodeAutomodeCmd(
  input: Partial<AutomodeCommandState> = {},
): Uint8Array {
  const state = mergeState(DEFAULT_AUTOMODE_COMMAND_STATE, input);
  return AutonomousModeCodec.encode(
    AutonomousModeCodec.create({
      header: buildHeader(BRAIN_TOPICS_COMMAND.AUTOMODE),
      operationMode: state.enabled
        ? AutonomousMode_OperationMode.AUTONOMOUS
        : AutonomousMode_OperationMode.MANUAL,
      autonomousMode: Number(state.autonomousMode || 0),
      errorCode: Number(state.errorCode || 0),
      warningCode: Number(state.warningCode || 0),
      activator: Number(state.activator || 0),
      userDefinedButton0: Number(state.userDefinedButton0 || 0),
      userDefinedButton1: Number(state.userDefinedButton1 || 0),
      buttonPushed: Number(state.buttonPushed || 0),
      mrmStatus: Number(state.mrmStatus || 0),
    }),
  ).finish();
}

export function encodeControlRequest(
  controllerId: string,
  requestState: ControlRequest_RequestStatus,
  remoteType: ControlRequest_CtrlType = ControlRequest_CtrlType.CTRL_DIRECT,
  headerTopic: string = BRAIN_TOPICS_CONTROL.REQUEST,
): Uint8Array {
  return ControlRequestCodec.encode(
    ControlRequestCodec.create({
      header: buildHeader(headerTopic),
      controllerId,
      requestState,
      remoteType,
    }),
  ).finish();
}

export function encodeMissionCmd(input: {
  type: MissionCmd_Type;
  subType?: string;
}): Uint8Array {
  return MissionCmdCodec.encode(
    MissionCmdCodec.create({
      header: buildHeader(BRAIN_TOPICS_COMMAND.MISSION),
      type: input.type,
      subType: input.subType || "",
    }),
  ).finish();
}

export function encodePathCmd(input?: {
  nodes?: PathNode[];
  behavior?: number;
  subtype?: number;
  state?: number;
}): Uint8Array {
  const state = input || {};
  return PathCodec.encode(
    PathCodec.create({
      header: buildHeader(BRAIN_TOPICS_COMMAND.PATH),
      nodes: state.nodes || DEFAULT_TEST_LAB_PATH_NODES,
      behavior: Number(state.behavior || 0),
      subtype: Number(state.subtype || 0),
      state: Number(state.state || 0),
    }),
  ).finish();
}

export function decodeVehicleState(payload: Uint8Array): VehicleState {
  return VehicleStateCodec.decode(payload);
}

export interface DecodedVehiclePosition {
  latitude: number;
  longitude: number;
  heading?: number;
}

/**
 * VehicleState 의 gnss(Wgs84) 에서 실시간 위치를 추출한다.
 * lat/lon 이 유한하고 (0,0) 이 아닐 때만 위치를 반환하고, 그렇지 않으면 null 을 반환해
 * 호출 측이 "위치 없음" 으로 처리하게 한다. gnss.status 는 판정에 쓰지 않는다(아래 주석).
 */
export function extractVehiclePosition(
  vehicleState: { gnss?: VehicleState["gnss"] } | null | undefined,
): DecodedVehiclePosition | null {
  const gnss = vehicleState?.gnss;
  if (!gnss) {
    return null;
  }

  // gnss.status 는 판정에 쓰지 않는다(요청).
  // proto 상으론 VALID=0 / INVALID=1 이지만, 0 이 protobuf 기본값이라 필드를 채우지 않는
  // 차량과 다른 규약(예: fix 품질 1=GPS, 2=RTK)으로 채우는 차량이 섞인다. status 를 신뢰해
  // 걸러내면 좌표가 멀쩡한데도 지도에서 사라지므로, "좌표 자체의 유효성"만으로 판정한다.
  const latitude = Number(gnss.latitude);
  const longitude = Number(gnss.longitude);
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    (latitude === 0 && longitude === 0)
  ) {
    return null;
  }

  const heading = Number(gnss.heading);
  return {
    latitude,
    longitude,
    ...(Number.isFinite(heading) ? { heading } : {}),
  };
}

export function decodeLateralCmd(payload: Uint8Array): LateralCmd {
  return LateralCmdCodec.decode(payload);
}

export function decodeLongitudinalCmd(payload: Uint8Array): LongitudinalCmd {
  return LongitudinalCmdCodec.decode(payload);
}

export function decodeAccessoryCmd(payload: Uint8Array): AccessoryCmd {
  return AccessoryCmdCodec.decode(payload);
}

export function decodeUserConfiguration(payload: Uint8Array): UserConfiguration {
  return UserConfigurationCodec.decode(payload);
}

export function decodeAutomode(payload: Uint8Array): AutonomousMode {
  return AutonomousModeCodec.decode(payload);
}

/**
 * std_msgs/String 디코드.
 *
 * /vehicle/status/control_mode 가 이 타입으로 "AD" | "MRM" | "REMOTE" 를 실어 온다.
 * 값 해석은 utils/control_mode 가 맡고, 여기서는 껍질만 벗긴다.
 */
export function decodeStdString(payload: Uint8Array): { data: string } {
  return StdStringCodec.decode(payload);
}

export function decodeKeyValue(payload: Uint8Array): KeyValue {
  return KeyValueCodec.decode(payload);
}

export function decodeCameraParams(payload: Uint8Array): KeyValueArray {
  return KeyValueArrayCodec.decode(payload);
}

export function decodeRemoteControl(payload: Uint8Array): RemoteControlStatus {
  return RemoteControlStatusCodec.decode(payload);
}

export function decodeControlStatus(payload: Uint8Array): ControlStatusType {
  return ControlStatusCodec.decode(payload);
}

export function decodeControlRequest(payload: Uint8Array): ControlRequest {
  return ControlRequestCodec.decode(payload);
}

export function decodeSystemCmd(payload: Uint8Array) {
  return SystemCmdCodec.decode(payload);
}

export function decodeAckInfo(payload: Uint8Array): AckInfo {
  return AckInfoCodec.decode(payload);
}

export function decodeVehicleGenericInfo(
  payload: Uint8Array,
): VehicleGenericInfo {
  return VehicleGenericInfoCodec.decode(payload);
}

export function decodeWaypoints(payload: Uint8Array): Waypoints {
  return WaypointsCodec.decode(payload);
}

export function decodeMission(payload: Uint8Array): Mission {
  return MissionCodec.decode(payload);
}

export function decodeKeyValueArray(payload: Uint8Array): KeyValueArray {
  return KeyValueArrayCodec.decode(payload);
}

export function extractIncomingHeader(
  envelope: HeaderEnvelopeLike,
): MessageHeaderLike | null {
  if (envelope.typeName === "brain_msgs.proto.ControlStatus") {
    return normalizeHeader(decodeControlStatus(envelope.payload)?.header);
  }

  if (envelope.typeName === "brain_msgs.proto.KeyValueArray") {
    return normalizeHeader(decodeCameraParams(envelope.payload)?.header);
  }

  if (envelope.typeName === "brain_msgs.proto.ControlRequest") {
    return normalizeHeader(decodeControlRequest(envelope.payload)?.header);
  }

  return null;
}

export {
  ControlRequest_RequestStatus,
  ControlRequest_CtrlType,
  ControlStatus_Status,
  ControlStatus_TransferStatus,
  Gear_GearType,
  MissionCmd_Type,
  RemoteControlStatus_RemoteType,
};

/**
 * 주행 로깅 전용 디코더 표 — 토픽의 proto type name 으로 골라 쓴다.
 *
 * 각 토픽마다 전용 decode 함수를 손으로 매칭하지 않는 이유: 토픽↔타입 대응은 이미
 * BRAIN_TOPIC_TYPE_NAMES 한 곳에 있고, 로깅이 그 표를 두 번째로 베껴 적으면 토픽이
 * 추가될 때마다 조용히 어긋난다.
 */
const LOG_DECODERS_BY_TYPE_NAME: Record<string, (payload: Uint8Array) => unknown> = {
  "brain_msgs.proto.LateralCmd": (p) => LateralCmdCodec.decode(p),
  "brain_msgs.proto.LongitudinalCmd": (p) => LongitudinalCmdCodec.decode(p),
  "brain_msgs.proto.AccessoryCmd": (p) => AccessoryCmdCodec.decode(p),
  "brain_msgs.proto.SystemCmd": (p) => SystemCmdCodec.decode(p),
  "brain_msgs.proto.Path": (p) => PathCodec.decode(p),
  "brain_msgs.proto.MissionCmd": (p) => MissionCmdCodec.decode(p),
  "brain_msgs.proto.Mission": (p) => MissionCodec.decode(p),
  "brain_msgs.proto.KeyValue": (p) => KeyValueCodec.decode(p),
  "brain_msgs.proto.KeyValueArray": (p) => KeyValueArrayCodec.decode(p),
  "brain_msgs.proto.AutonomousMode": (p) => AutonomousModeCodec.decode(p),
  "brain_msgs.proto.ControlRequest": (p) => ControlRequestCodec.decode(p),
  "brain_msgs.proto.ControlStatus": (p) => ControlStatusCodec.decode(p),
  "brain_msgs.proto.VehicleState": (p) => VehicleStateCodec.decode(p),
  "brain_msgs.proto.Waypoints": (p) => WaypointsCodec.decode(p),
  "brain_system_msgs.proto.AckInfo": (p) => AckInfoCodec.decode(p),
  "brain_system_msgs.proto.RemoteControlStatus": (p) =>
    RemoteControlStatusCodec.decode(p),
  "brain_sensor_msgs.proto.UserConfiguration": (p) =>
    UserConfigurationCodec.decode(p),
  "brain_sensor_msgs.proto.VehicleGenericInfo": (p) =>
    VehicleGenericInfoCodec.decode(p),
  "std_msgs.proto.String": (p) => StdStringCodec.decode(p),
};

// 카메라 settings 토픽은 타입 표에 없다(토픽 이름 자체가 media/<cam>/settings 형태).
// 실제 페이로드는 KeyValueArray 이므로 여기서만 보충한다.
const CAMERA_SETTINGS_TYPE_NAME = "brain_msgs.proto.KeyValueArray";

/**
 * 주행 로그에 남길 형태로 payload 를 푼다.
 *
 * 디코더가 없거나 파싱이 실패해도 던지지 않는다 — 로깅 때문에 MQTT 전송/수신 경로가
 * 멈추는 일은 없어야 한다. 그런 경우 바이트 길이만 남겨 "왔다는 사실" 은 보존한다.
 */
export function decodeBrainPayloadForLog(
  topic: string,
  payload: Uint8Array,
): unknown {
  const typeName = ALL_CAMERA_SETTINGS_TOPICS.includes(topic as CameraSettingsTopic)
    ? CAMERA_SETTINGS_TYPE_NAME
    : BRAIN_TOPIC_TYPE_NAMES[topic];
  const decoder = typeName ? LOG_DECODERS_BY_TYPE_NAME[typeName] : undefined;
  if (!decoder) {
    return { typeName, bytes: payload.byteLength };
  }
  try {
    return decoder(payload);
  } catch {
    return { typeName, bytes: payload.byteLength, decodeError: true };
  }
}
